import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  Ticket, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  Search,
  Building2,
  Briefcase,
  MapPin,
  FolderTree,
  Tag,
  Truck,
  Package2,
  Database,
  Clock,
  BookOpen,
  UserCog,
  LucideIcon,
  User,
  ScrollText,
  Activity,
  Wrench,
  ClipboardCheck,
  ClipboardList,
  FileText,
  BarChart3,
  Shield,
  Upload,
  Key,
  ShoppingCart,
  ShieldCheck,
  TrendingDown,
  QrCode,
  Lock,
  Stamp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { Input } from '@/components/ui/input';
import { NotificationBell } from '@/features/notifications';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigationSections: NavSection[] = [
  {
    title: 'Main Menu',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Assets', href: '/assets', icon: Package },
      { name: 'Tickets', href: '/tickets', icon: Ticket },
      { name: 'Employees', href: '/employees', icon: Users },
      { name: 'Scanner', href: '/scanner', icon: QrCode },
    ],
  },
  {
    title: 'Workflows',
    items: [
      { name: 'Access Forms', href: '/access-forms', icon: ClipboardCheck },
      { name: 'Asset Issue Forms', href: '/asset-issue-forms', icon: ClipboardList },
      { name: 'Repairs', href: '/repairs', icon: Wrench },
      { name: 'Service Providers', href: '/service-providers', icon: Truck },
    ],
  },
  {
    title: 'Documents',
    items: [
      { name: 'All Documents', href: '/documents', icon: FileText },
    ],
  },
  {
    title: 'Organization',
    items: [
      { name: 'Branches', href: '/branches', icon: Building2 },
      { name: 'Departments', href: '/departments', icon: Briefcase },
      { name: 'Locations', href: '/locations', icon: MapPin },
      { name: 'Org Settings', href: '/organization', icon: Building2 },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { name: 'Categories', href: '/categories', icon: FolderTree },
      { name: 'Brands', href: '/brands', icon: Tag },
      { name: 'Suppliers', href: '/vendors', icon: Truck },
      { name: 'Consumables', href: '/consumables', icon: Package2 },
      { name: 'Licenses', href: '/licenses', icon: Key },
      { name: 'Purchase Orders', href: '/purchase-orders', icon: ShoppingCart },
      { name: 'Asset Marking', href: '/marking', icon: Stamp },
      { name: 'Password Vault', href: '/password-vault', icon: Lock },
    ],
  },
  {
    title: 'Support',
    items: [
      { name: 'SLA Policies', href: '/sla-policies', icon: Clock },
      { name: 'Knowledge Base', href: '/knowledge-base', icon: BookOpen },
    ],
  },
  {
    title: 'Analytics',
    items: [
      { name: 'Reports', href: '/reports', icon: BarChart3 },
      { name: 'Depreciation', href: '/depreciation', icon: TrendingDown },
      { name: 'Warranty Alerts', href: '/warranty-alerts', icon: ShieldCheck },
    ],
  },
  {
    title: 'Administration',
    items: [
      { name: 'Users', href: '/users', icon: UserCog },
      { name: 'Permissions', href: '/permissions', icon: Shield },
      { name: 'Import Data', href: '/import', icon: Upload },
      { name: 'Audit Logs', href: '/audit-logs', icon: ScrollText },
      { name: 'Activity Logs', href: '/activity-logs', icon: Activity },
      { name: 'Backups', href: '/backups', icon: Database },
      { name: 'Settings', href: '/settings', icon: Settings },
    ],
  },
];

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-200 ease-in-out lg:translate-x-0 flex flex-col",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            {user?.organization?.logo ? (
              <img 
                src={`${import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace(/\/api$/, '') : ''}${user.organization.logo}`}
                alt={user.organization.name || 'Organization'}
                className="h-8 w-auto max-w-[140px] object-contain"
              />
            ) : (
              <>
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-lg">
                  {user?.organization?.name || 'Asset Manager'}
                </span>
              </>
            )}
          </div>
          <button 
            className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
          {navigationSections.map((section) => (
            <div key={section.title}>
              <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                {section.title}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.href || 
                    location.pathname.startsWith(item.href + '/');
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary border-l-2 border-primary"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className={cn("w-5 h-5", isActive && "text-primary")} />
                      {item.name}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-medium text-primary">
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user?.role}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="h-full px-4 flex items-center justify-between gap-4">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Search */}
            <div className="flex-1 max-w-md hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search assets, tickets..."
                  className="pl-10"
                />
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <NotificationBell />

              {/* User menu */}
              <div className="relative">
                <button
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </span>
                  </div>
                  <span className="hidden md:block text-sm font-medium">
                    {user?.firstName}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {userMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                      </div>
                      <NavLink
                        to="/profile"
                        className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        My Profile
                      </NavLink>
                      <NavLink
                        to="/settings"
                        className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </NavLink>
                      <button
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={handleLogout}
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
